import { FC, useEffect, useState, useCallback, useMemo } from 'react'
import { useChat } from '../../contexts/ChatContext'
import { clinicalCommands, type ClinicalCommand } from '../../services/commandRegistry'
import { cn } from '../../lib/utils'

interface CommandPickerProps {
  onSelect: (command: ClinicalCommand) => void
}

/**
 * Command picker component - shows available clinical commands when user types /
 * Does NOT steal focus from input - keyboard navigation handled via global listeners
 */
export const CommandPicker: FC<CommandPickerProps> = ({ onSelect }) => {
  const { 
    isCommandPickerOpen, 
    commandPrefix,
    currentPatient,
    setCommandPickerOpen
  } = useChat()
  
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Memoize filtered commands to prevent unnecessary re-renders and callback recreations
  const filteredCommands = useMemo(() => 
    clinicalCommands.filter(cmd => {
      const matchesSearch = cmd.trigger.toLowerCase().includes(commandPrefix.toLowerCase()) ||
                           cmd.name.toLowerCase().includes(commandPrefix.toLowerCase())
      const matchesContext = !cmd.requiresPatient || !!currentPatient
      return matchesSearch && matchesContext
    }), [commandPrefix, currentPatient]
  )

  // Reset selection when filtered commands change (prefix or patient context)
  useEffect(() => {
    setSelectedIndex(0)
  }, [filteredCommands])

  // Keyboard navigation - attached to window so input keeps focus
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isCommandPickerOpen || filteredCommands.length === 0) return

    if (e.key === 'Escape') {
      e.preventDefault()
      setCommandPickerOpen(false)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      onSelect(filteredCommands[selectedIndex])
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => (prev + 1) % filteredCommands.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => prev === 0 ? filteredCommands.length - 1 : prev - 1)
    } else if (e.key === 'Tab') {
      e.preventDefault()
      setSelectedIndex(prev => (prev + 1) % filteredCommands.length)
    }
  }, [isCommandPickerOpen, filteredCommands, selectedIndex, onSelect, setCommandPickerOpen])

  // Attach keyboard listener when picker is open
  useEffect(() => {
    if (isCommandPickerOpen) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isCommandPickerOpen, handleKeyDown])

  if (!isCommandPickerOpen) return null

  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 rounded-xl border-2 bg-background p-2 shadow-lg">
      {filteredCommands.length === 0 ? (
        <div className="flex h-14 items-center justify-center text-sm italic text-muted-foreground">
          {currentPatient 
            ? 'No matching commands found.'
            : 'Some commands require patient context.'}
        </div>
      ) : (
        <div className="flex flex-col space-y-1">
          {filteredCommands.map((command, index) => (
            <div
              key={command.trigger}
              className={cn(
                "flex cursor-pointer flex-col rounded p-3 transition-colors",
                "hover:bg-accent",
                index === selectedIndex && "bg-accent"
              )}
              onClick={() => onSelect(command)}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-semibold text-primary">
                  {command.trigger}
                </span>
                <span className="font-semibold">{command.name}</span>
                {command.category === 'custom' && (
                  <span className="ml-auto rounded-full bg-primary/20 px-2 py-0.5 text-xs">
                    Custom
                  </span>
                )}
                {command.requiresPatient && !currentPatient && (
                  <span className="ml-auto rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs">
                    Needs Patient
                  </span>
                )}
              </div>
              
              <div className="mt-1 truncate text-sm text-muted-foreground">
                {command.description}
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-2 border-t pt-2 text-xs text-muted-foreground">
        <div className="flex items-center justify-between px-2">
          <span>↑↓ Navigate • Tab Cycle • Enter Select • Esc Close</span>
          <span className="font-semibold">{filteredCommands.length} commands</span>
        </div>
      </div>
    </div>
  )
}
